/**
 * The pure view-model behind "the floor": every other member's night as a
 * public chip desk. The quotes read the same surfaces the TV board does —
 * the public leaderboard and the public balance timeline — so a phone sees
 * what the room can already read off the felt, and nothing behind a
 * credential: no trades, no ledger tape. No React, no fetching.
 */
import type { BalanceSeries, CasinoLeaderboardEntry } from '@tv-casino/shared';
import { profileHex } from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';
import { boardDeskModel, type MemberMarketVM } from './memberMarketModel.js';

export interface FloorPlayerRow {
  memberId: string;
  name: string;
  /** Their profile hue, for the dot beside the name. Null when unpicked. */
  accentHex: string | null;
  /** A signed-in account plays under this membership: the profile badge. */
  hasProfile: boolean;
  /** The night's signed result, printed next to the name. */
  netLabel: string;
  tone: 'win' | 'loss' | 'even';
  /** Quote + sparkline + night metrics, quote-only. */
  market: MemberMarketVM;
}

export interface FloorMarketVM {
  /** False until the board lands; the section shows its loading line. */
  ready: boolean;
  /** Printed instead of the rows when nobody else has played yet. */
  emptyLine: string | null;
  rows: FloorPlayerRow[];
}

const toneOf = (n: number): 'win' | 'loss' | 'even' => (n > 0 ? 'win' : n < 0 ? 'loss' : 'even');

/**
 * Fold the public board and the night's lines into one floor, best net
 * first as the leaderboard returns it. This phone's own desk is left out —
 * it already has the full one at the top of the stats tab.
 */
export function floorMarketModel(
  board: CasinoLeaderboardEntry[] | null,
  series: BalanceSeries[],
  myMemberId: string | null,
): FloorMarketVM {
  if (board === null) return { ready: false, emptyLine: null, rows: [] };
  const line = new Map(series.map((s) => [s.memberId, s]));
  const rows = board
    .filter((entry) => entry.memberId !== myMemberId)
    .map((entry) => ({
      memberId: entry.memberId,
      name: entry.name,
      accentHex: entry.color ? profileHex(entry.color) : null,
      hasProfile: entry.hasProfile,
      netLabel: formatSignedChips(entry.net),
      tone: toneOf(entry.net),
      market: boardDeskModel(entry, line.get(entry.memberId) ?? null),
    }));
  return {
    ready: true,
    emptyLine: rows.length === 0 ? 'Nobody else has played yet.' : null,
    rows,
  };
}
